import styles from './FilterSubGroupList.module.scss';

import ChevronRight from '@mui/icons-material/ChevronRight';
import { List, ListItem, ListItemIcon, ListItemText } from '@mui/material';
import { Column } from '@tanstack/react-table';
import i18next from 'i18next';
import React, { useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { FilterType } from '@work4all/components';
import {
  IStackItem,
  useHistoryStack,
} from '@work4all/components/lib/navigation/history-stack';

import { FilterOverlayContext } from '../../FiltersOverlayV8';

const getLeavesAndTwigs = (
  columns: Column<unknown>[],
  currentPath: string[]
): { leaves: Column<unknown>[]; twigs: string[] } => {
  const result = { leaves: [], twigs: [] };

  const currentPathString = currentPath.join('.');

  columns.forEach((col) => {
    if (!col.getCanFilter() || (col.getFilterValue() as any)?.readOnly) {
      return;
    }

    const subGroupPathString = col.columnDef.filterSubgroupPath
      ? col.columnDef.filterSubgroupPath.join('.')
      : '';

    if (subGroupPathString === currentPathString) {
      result.leaves.push(col);
    } else if (
      subGroupPathString.startsWith(currentPathString) &&
      col.columnDef.filterSubgroupPath.length - currentPath.length === 1 &&
      result.twigs.indexOf(
        col.columnDef.filterSubgroupPath[currentPath.length]
      ) === -1
    ) {
      result.twigs.push(col.columnDef.filterSubgroupPath[currentPath.length]);
    }
  });

  return result;
};

const goToSubgroup = (
  subgroupPath: string[],
  go: (item: IStackItem) => void
) => {
  go({
    title: i18next.t(subgroupPath[subgroupPath.length - 1]),
    view: <FilterSubGroupListV8 subgroupPath={subgroupPath} />,
  });
};

interface IFilterSubGroupListProps {
  subgroupPath: string[];
}

export const FilterSubGroupListV8: React.FC<IFilterSubGroupListProps> = (
  props
) => {
  const context = useContext(FilterOverlayContext);
  const { subgroupPath } = props;
  const { go, goBack } = useHistoryStack();

  const { t } = useTranslation();

  const columns = context.table.getAllColumns();

  const leavesAndTwigs = useMemo(() => {
    return getLeavesAndTwigs(columns, subgroupPath);
    // return getLeavesAndTwigs(columns, subgroupPath);
  }, [columns, subgroupPath]);

  return (
    <List>
      {leavesAndTwigs.leaves.map((col) => {
        return !col.getIsFiltered() ||
          // Explicitly show the ClosedStatus filter if it is set to `false`. For
          // ClosedStatus filter type the value `false` acts basically the same as
          // "unset" for other filters.
          (col.columnDef.filterType === FilterType.ClosedStatus &&
            (col.getFilterValue() as any)?.value === false) ? (
          <ListItem
            key={col.columnDef.header as string}
            className={styles.item}
            button
            divider={false}
            onClick={() => {
              context.addAdditionalFilter(col.columnDef.header as string);
              goBack(0);
            }}
          >
            <ListItemText
              className={styles.itemText}
              classes={{ primary: styles.primary }}
            >
              <div className={styles.itemText}>
                <div className={styles.filterNavLabel}>
                  {col.columnDef.header as string}
                </div>
              </div>
            </ListItemText>
          </ListItem>
        ) : null;
      })}
      {leavesAndTwigs.twigs.map((subgroup) => {
        return (
          <ListItem
            key={subgroup}
            className={styles.item}
            button
            divider={false}
            onClick={() => {
              goToSubgroup([...subgroupPath, subgroup], go);
            }}
          >
            <ListItemText
              className={styles.itemText}
              classes={{ primary: styles.primary }}
            >
              {t(subgroup)}
            </ListItemText>
            <ListItemIcon>
              <ChevronRight></ChevronRight>
            </ListItemIcon>
          </ListItem>
        );
      })}
    </List>
  );
};
